import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Button } from '../ui/button';
import { useDashboardLayout } from '../../contexts/DashboardLayoutContext';

const PADDING = 8;

// Maps targetDataAttribute values to sidebar actions needed to reveal them
const SIDEBAR_TARGETS = {
  'admin-nav-link': { openSidebar: true, expandMenus: ['Superadmin', 'Admin'] },
  'admin-users-link': { openSidebar: true, expandMenus: ['Superadmin', 'Admin', 'User Management'] },
  'admin-access-link': { openSidebar: true, expandMenus: ['Superadmin', 'Admin', 'User Management'] },
  'executives-nav-link': { openSidebar: true },
  'trips-nav-link': { openSidebar: true },
  'todos-nav-link': { openSidebar: true },
  'email-settings-link': { openSidebar: true, expandMenus: ['Utilities'] },
};

function getTooltipStyle(targetRect, position, tooltipRef) {
  if (!targetRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  const tooltipEl = tooltipRef?.current;
  const tooltipWidth = tooltipEl?.offsetWidth || 320;
  const tooltipHeight = tooltipEl?.offsetHeight || 200;
  const gap = 12;

  const style = { position: 'fixed' };

  switch (position) {
    case 'top':
      style.left = Math.max(8, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 8));
      style.top = Math.max(8, targetRect.top - tooltipHeight - gap);
      break;
    case 'left':
      style.left = Math.max(8, targetRect.left - tooltipWidth - gap);
      style.top = Math.max(8, targetRect.top + targetRect.height / 2 - tooltipHeight / 2);
      break;
    case 'right':
      style.left = Math.min(targetRect.right + gap, window.innerWidth - tooltipWidth - 8);
      style.top = Math.max(8, targetRect.top + targetRect.height / 2 - tooltipHeight / 2);
      break;
    case 'bottom':
    default:
      style.left = Math.max(8, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, window.innerWidth - tooltipWidth - 8));
      style.top = Math.min(targetRect.bottom + gap, window.innerHeight - tooltipHeight - 8);
      break;
  }

  return style;
}

function isElementInViewport(rect) {
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth
  );
}

export default function TutorialSpotlight({ step, onNext, onPrevious, onSkip, showPrevious }) {
  const [targetRect, setTargetRect] = useState(null);
  const [targetFound, setTargetFound] = useState(false);
  const tooltipRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardLayout = useDashboardLayout();

  // Keep the layout context in a ref so the sidebar effect below doesn't
  // re-run (and re-dispatch) every time the context value's identity changes
  const dashboardLayoutRef = useRef(dashboardLayout);
  dashboardLayoutRef.current = dashboardLayout;

  // Navigate to the step's route if specified and different from current
  useEffect(() => {
    if (step?.route && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [step, location.pathname, navigate]);

  // Open sidebar and expand menus if this target is in the sidebar —
  // once per step, not on every layout-context change
  useEffect(() => {
    if (!step) return;
    const layout = dashboardLayoutRef.current;
    if (!layout) return;

    const sidebarAction = SIDEBAR_TARGETS[step.targetDataAttribute];
    if (sidebarAction?.openSidebar) {
      layout.openSidebar();
      if (sidebarAction.expandMenus) {
        sidebarAction.expandMenus.forEach(menu => layout.expandMenu(menu));
      }
    } else {
      // Close sidebar if this step doesn't need it
      layout.closeSidebar();
    }
  }, [step]);

  const updateTargetRect = useCallback(() => {
    if (!step?.targetDataAttribute) return;

    const el = document.querySelector(`[data-tutorial="${step.targetDataAttribute}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      // Ensure element has dimensions (not display:none)
      if (rect.width > 0 && rect.height > 0) {
        setTargetRect(rect);
        setTargetFound(true);

        // Scroll into view if not visible
        if (!isElementInViewport(rect)) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        return;
      }
    }
    setTargetFound(false);
    setTargetRect(null);
  }, [step?.targetDataAttribute]);

  // Find and track the target element
  useEffect(() => {
    updateTargetRect();

    // Re-measure on scroll/resize
    const handleUpdate = () => updateTargetRect();
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    // Also observe DOM changes in case element mounts late (e.g. sidebar opening)
    const observer = new MutationObserver(() => {
      updateTargetRect();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
      observer.disconnect();
    };
  }, [updateTargetRect]);

  if (!step) return null;

  const padding = step.highlightPadding || PADDING;
  const tooltipPosition = step.tooltipPosition || 'bottom';

  // Fallback: if target not found, render as a centered card
  if (!targetFound || !targetRect) {
    return (
      <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60">
        <motion.div
          ref={tooltipRef}
          initial={prefersReducedMotion ? {} : { opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-background border rounded-lg shadow-xl p-6 max-w-sm mx-4"
          role="tooltip"
          aria-labelledby="tutorial-spotlight-title"
          aria-describedby="tutorial-spotlight-desc"
        >
          <h3 id="tutorial-spotlight-title" className="font-semibold text-lg mb-2">{step.title}</h3>
          <p id="tutorial-spotlight-desc" className="text-sm text-muted-foreground mb-4">{step.description}</p>
          <div className="flex justify-between gap-2">
            {showPrevious && (
              <Button variant="ghost" size="sm" onClick={onPrevious}>Back</Button>
            )}
            <div className="flex gap-2 ml-auto">
              {step.actions?.secondary && (
                <Button variant="ghost" size="sm" onClick={onSkip}>
                  {step.actions.secondary.label}
                </Button>
              )}
              {step.actions?.primary && (
                <Button size="sm" onClick={onNext}>
                  {step.actions.primary.label}
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  const spotlightStyle = {
    position: 'fixed',
    top: targetRect.top - padding,
    left: targetRect.left - padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
    borderRadius: 8,
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
    pointerEvents: 'none',
    zIndex: 90,
  };

  return (
    <>
      {/* Spotlight cutout overlay */}
      <motion.div
        style={spotlightStyle}
        initial={prefersReducedMotion ? {} : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        aria-hidden="true"
      />

      {/* Click blocker on the overlay area (outside the spotlight) */}
      <div
        className="fixed inset-0 z-[89]"
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        aria-hidden="true"
      />

      {/* Tooltip card */}
      <motion.div
        ref={tooltipRef}
        style={getTooltipStyle(targetRect, tooltipPosition, tooltipRef)}
        className="z-[91] bg-background border rounded-lg shadow-xl p-4 max-w-xs"
        initial={prefersReducedMotion ? {} : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.15 }}
        role="tooltip"
        aria-labelledby="tutorial-spotlight-title"
        aria-describedby="tutorial-spotlight-desc"
      >
        <h3 id="tutorial-spotlight-title" className="font-semibold text-base mb-1.5">{step.title}</h3>
        <p id="tutorial-spotlight-desc" className="text-sm text-muted-foreground mb-4">{step.description}</p>
        <div className="flex justify-between gap-2">
          {showPrevious && (
            <Button variant="ghost" size="sm" onClick={onPrevious}>Back</Button>
          )}
          <div className="flex gap-2 ml-auto">
            {step.actions?.secondary && (
              <Button variant="ghost" size="sm" onClick={onSkip}>
                {step.actions.secondary.label}
              </Button>
            )}
            {step.actions?.primary && (
              <Button size="sm" onClick={onNext}>
                {step.actions.primary.label}
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
}
